import { useEffect, useState } from 'react';
import { Box, Typography, Button, Card, Tooltip } from '@mui/material';
import {
  getOtherFeeDetails,
  getOtherFeeTypesConfigurations,
  postOtherFeesDetails,
} from '../../../features/payments/PaymentSlice';
import { useDispatch } from 'react-redux';
import Spinner from '../../../components/UIElements/Spinner';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import useNotification from '../../../hooks/useNotification';
import ToastNotification from '../../../components/UIElements/ToastNotification';
import helperFunctions from '../../../utils/helperFunctions';
import ModalDialog from '../../../components/UIElements/ModalDialog';
import AddFeePayment from '../../../components/AddFeePayment';
import { DataGrid } from '@mui/x-data-grid';
import PersonIcon from '@mui/icons-material/Person';
export default function StudentOtherFees({ StudentId }) {
  const dispatch = useDispatch();
  const [feesConfigurations, setFeesConfigurations] = useState('');
  const [feesHistory, setFeesHistory] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [history, setHistory] = useState(false);
  const [paymentType, setPaymentType] = useState('');
  const [balance, setBalance] = useState('no');
  const [totalAmount, setTotalAmount] = useState(0);
  const { formatNumber } = helperFunctions;
  const { notification, showSuccess, showError, hideNotification } =
    useNotification();
  const status = localStorage.getItem('StudentStatus');
  const fetchFeesConfigurations = async () => {
    try {
      setLoading(true);
      const res = await dispatch(
        getOtherFeeTypesConfigurations(StudentId),
      ).unwrap();
      setFeesConfigurations(res.data);
      const total = res.data?.reduce((sum, f) => sum + Number(f.amount), 0);
      setTotalAmount(total);
    } catch (err) {
      console.error(err);
      showError('Error in fetching Fees Details');
    } finally {
      setLoading(false);
    }
  };
  const fetchFeesTransactions = async () => {
    try {
      setHistory(true);
      const res = await dispatch(getOtherFeeDetails(StudentId)).unwrap();
      setFeesHistory(res.data);
    } catch (err) {
      console.error(err);
      showError('Error in fetching Fees transactions.');
    } finally {
      setHistory(false);
    }
  };
  async function handleSubmit(data) {
    try {
      setIsSubmitting(true);
      const res = await dispatch(
        postOtherFeesDetails({
          studentId: StudentId,
          feeType: paymentType, // type of the others fee
          receiptNumber: data.transactionReference,
          paymentMode: data.paymentType,
          amount: data.amount,
        }),
      ).unwrap();
      if (res.status == 200) {
        showSuccess('Payment saved successfully.');
        setPaymentType('');
        setBalance('');
        return true;
      } else return false;
    } catch (err) {
      console.error(err);
      if (err?.status === 409)
        showError(
          'Entered amount is greater than pending amount, Please check again !',
        );
      showError('Error in saving the payment details.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }
  async function callBoth() {
    await Promise.all([fetchFeesConfigurations(), fetchFeesTransactions()]);
    console.log('Both called!');
  }

  useEffect(() => {
    if (StudentId) callBoth();
  }, [StudentId]);
  if (loading)
    return (
      <Card className='relative min-h-80 border'>
        <Spinner
          asOverlay={true}
          text={`Fetching the Total Fees Details of ${StudentId}`}
        />
      </Card>
    );
  if (!StudentId)
    return (
      <Card className='flex flex-col justify-center items-center h-60 border rounded-lg'>
        <PersonIcon className='text-gray-300 mb-1' style={{ fontSize: 90 }} />
        <Typography variant='h6' className='text-gray-400 mb-2'>
          No Student Selected
        </Typography>
        <Typography variant='body2' className='text-gray-500'>
          Please search for a student to know payment details.
        </Typography>
      </Card>
    );
  if (!feesConfigurations && !feesHistory)
    return (
      <Card className='flex justify-center items-center h-60 border rounded-lg'>
        <Typography variant='body1'>Student details not found</Typography>
      </Card>
    );
  return (
    <>
      <Card className='max-w-4xl mx-auto p-6 border'>
        {/* Header */}
        <Box className='mb-4'>
          <Typography variant='h6' className='text-blue-700 font-bold'>
            Manage the all types of payments here
          </Typography>
          <Typography variant='body2' className='text-gray-600'>
            Clikc the pay Fee button to pay amount under that corersponding
            sections respectively...
          </Typography>
        </Box>

        <div className='relative min-h-40 w-full '>
          <>
            {/* Fee Types Configuration Table */}
            {feesConfigurations?.length == 0 ? (
              <Typography>
                No Additional Fees Configured to this student
              </Typography>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  variant='outlined'
                  sx={{
                    maxHeight: 400,
                    overflow: 'auto',
                  }}
                >
                  <Table stickyHeader size='small' sx={{ minWidth: 700 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                        <TableCell className='text-blue-600'>
                          <strong>SNO</strong>
                        </TableCell>{' '}
                        <TableCell className='text-blue-600'>
                          <strong>Fee Type</strong>
                        </TableCell>{' '}
                        <TableCell className='text-blue-600'>
                          <strong>Amount</strong>
                        </TableCell>{' '}
                        <TableCell className='text-blue-600'>
                          <strong>Paid</strong>
                        </TableCell>{' '}
                        <TableCell className='text-blue-600'>
                          <strong>Due Amount</strong>
                        </TableCell>{' '}
                        <TableCell className='text-blue-600'>
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {feesConfigurations?.map((feeType, index) => (
                        <TableRow
                          key={feeType?.id}
                          hover
                          sx={{
                            '&:nth-of-type(odd)': {
                              backgroundColor: '#fafafa',
                            },
                          }}
                          className={`${feeType.id ? '' : 'hidden'}`}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{feeType.feeName}</TableCell>
                          <TableCell>{`${formatNumber(feeType.amount)}/-`}</TableCell>
                          <TableCell>{`${formatNumber(feeType.amount - feeType.due)}/-`}</TableCell>
                          <TableCell>{`${formatNumber(feeType.due)}/-`}</TableCell>

                          <TableCell>
                            <Button
                              disabled={
                                feeType?.due == 0 ||
                                status === 'DROPPED_OUT' ||
                                status === 'PASSED_OUT' ||
                                status === 'DELETED'
                              }
                              onClick={() => {
                                setPaymentType(feeType.id);
                                setBalance(feeType.due);
                              }}
                              variant='contained'
                            >
                              Pay
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* Total */}
                <div className='flex justify-end mt-6 font-semibold text-lg'>
                  Amount to Paid: ₹ {totalAmount}
                </div>
              </>
            )}
          </>
        </div>

        {/* Model For Uploading the Fee Details */}
        <ModalDialog
          size='sm'
          isOpen={paymentType > 0}
          onClose={() => setPaymentType('')}
          title='Add Fee Payment'
        >
          <AddFeePayment
            onSubmission={handleSubmit}
            onClose={() => {
              setPaymentType('');
              callBoth();
            }}
            balance={balance}
            writeOff={false}
            isSubmitting={isSubmitting}
          />
        </ModalDialog>
        <ToastNotification
          open={notification.open}
          onClose={hideNotification}
          message={notification.message}
          title={notification.title}
          severity={notification.severity}
          autoHideDuration={notification.autoHideDuration}
          position={notification.position}
        />
      </Card>
      <Card className='max-w-4xl mx-auto p-6 border mt-6'>
        {/* Header */}
        <Box className='mb-4'>
          <Typography variant='h6' className='text-blue-700 font-bold'>
            Payments History
          </Typography>
        </Box>

        <div className='relative min-h-40 w-full '>
          {history ? (
            <Spinner
              asOverlay={true}
              text={`Fetching payments history of ${StudentId}`}
            />
          ) : (
            <>
              {/* Payments Table */}
              <div className='w-full overflow-auto'>
                <div className='border mt-2 rounded  h-auto min-w-[900px] '>
                  <DataGrid
                    sx={{
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#1976d2',
                      },
                      maxHeight: '70vh',
                      '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
                        {
                          outline: 'none',
                          '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#ffffff',
                          },
                        },
                      ' .MuiDataGrid-columnHeader': {
                        backgroundColor: '#f9faff',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                      },
                      '.MuiDataGrid-virtualScroller': {
                        maxHeight: 'calc(70vh - 120px)',
                        overflow: 'auto',
                      },
                    }}
                    rows={
                      Array.isArray(feesHistory)
                        ? [...feesHistory]?.reverse()
                        : []
                    }
                    columns={[
                      {
                        field: 'receiptNumber',
                        headerName: 'Receipt No',
                        flex: 1,
                        sortable: false,
                      },
                      {
                        field: 'paidOn',
                        headerName: 'Paid On',
                        flex: 1,
                        sortable: false,
                      },
                      {
                        field: 'feeType',
                        headerName: 'Fee Type',
                        flex: 1,
                        sortable: false,
                      },
                      {
                        field: 'amount',
                        headerName: 'Amount',
                        flex: 1,
                        sortable: false,
                      },
                      {
                        field: 'dueAmount',
                        headerName: 'Due Amount',
                        flex: 1,
                        sortable: false,
                      },
                      {
                        field: 'paymentMode',
                        headerName: 'Payment Type',
                        flex: 1,
                        sortable: false,
                      },
                      {
                        field: 'createdBy',
                        headerName: 'Operator',
                        flex: 1,
                        sortable: false,
                        renderCell: (param) => {
                          return (
                            <Tooltip title={param?.row?.email ?? 'No email'}>
                              {param?.row?.createdBy}
                            </Tooltip>
                          );
                        },
                      },
                      // {
                      //   field: '',
                      //   headerName: 'Actions',
                      //   flex: 1,
                      //   sortable: false,
                      //   minWidth: 100,
                      //   renderCell: (param) => (
                      //     <div className='flex items-center cursor-pointer'>
                      //       <Tooltip
                      //         title='Download Transaction Receipt'
                      //         enterDelay={0}
                      //       >
                      //         <IconButton
                      //           onClick={() => {
                      //             setLoading(true);
                      //             downloadReceipt(param);
                      //           }}
                      //           className=' text-red-600'
                      //         >
                      //           <BsFileEarmarkPdf />
                      //         </IconButton>
                      //       </Tooltip>
                      //       {userDetails &&
                      //         userDetails?.role?.toLowerCase() === 'admin' && (
                      //           <Tooltip
                      //             title='Delete Transaction '
                      //             enterDelay={0}
                      //           >
                      //             <IconButton
                      //               onClick={() => {
                      //                 setDeleteModal(param?.row?.id);
                      //               }}
                      //               className=' text-red-500'
                      //             >
                      //               <DeleteForeverIcon />
                      //             </IconButton>
                      //           </Tooltip>
                      //         )}
                      //       {/* <span>Download Receipt</span> */}
                      //     </div>
                      //   ),
                      // },
                    ]}
                    hideFooter={true}
                    disableColumnMenu
                    getRowClassName={(params) =>
                      params.row.dueAmount ===
                      (feesHistory?.length > 0
                        ? feesHistory?.at(-1).dueAmount
                        : [])
                        ? 'bg-gray-100 font-semibold'
                        : ''
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </Card>
    </>
  );
}
